"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const WidgetError_1 = require("../Error/WidgetError");
const ValidationError_1 = require("../Error/ValidationError");
const MappedModelBase_1 = require("../Model/MappedModelBase");
const FieldValidationError_1 = require("../Error/FieldValidationError");
const FieldValidationTypes_1 = require("../Enum/FieldValidationTypes");
/**
 * Validator for mapped objects.
 */
class MappingValidator {
    /**
     * Validates the object.
     *
     * @param {MappedModelBase | object} object
     * @param errorMessage
     * @param {IMapping} mapping
     */
    validate(object, errorMessage = "Invalid object.", mapping) {
        if (object instanceof MappedModelBase_1.default && !mapping) {
            mapping = object.constructor.getValueMapping({});
        }
        if (!mapping) {
            throw new WidgetError_1.default(`Validation mapping not provided.`);
        }
        const errors = this.doValidate(object, mapping, object);
        if (Object.keys(errors).length) {
            const errorsString = Object.entries(errors)
                .map(([key, value]) => key + ": " + value)
                .join("\n");
            // @TODO: should we throw or let the caller decide?
            throw new ValidationError_1.default(errorMessage + " : " + errorsString, errors, object);
        }
    }
    /**
     * Does the recursive validation.
     *
     * @param object
     * @param mapping
     * @param {string[]} atKey
     * @return {*}
     */
    doValidate(object, mapping, primaryObject, atKey = []) {
        if (!mapping.type) {
            throw new WidgetError_1.default("Malformed config mapping. Missing / bad type.");
        }
        // Option to skip validation.
        if (mapping.validation_condition && !mapping.validation_condition(primaryObject)) {
            return;
        }
        let errors = {};
        // Keys are by default required.
        if ((mapping.required === undefined || mapping.required) && (object === undefined)) {
            errors[atKey.join(".")] = [new FieldValidationError_1.default("Missing required value.", FieldValidationTypes_1.FieldValidationTypes.REQUIRED, mapping.type, atKey.join("."))];
            return errors;
        }
        else if (object === null || object === undefined) {
            return errors;
        }
        // Allow custom validations.
        let customValidations;
        if (typeof mapping.custom_validator === "function" && (customValidations = mapping.custom_validator(object))) {
            errors[atKey.join(".")] = customValidations;
        }
        switch (mapping.type) {
            case "object":
                errors = Object.assign(errors, this.validateObject(object, mapping, primaryObject, atKey));
                break;
            case "array":
                errors = Object.assign(errors, this.validateArray(object, mapping, primaryObject, atKey));
                break;
            case "string":
                errors = Object.assign(errors, this.validateString(object, mapping, atKey));
                break;
            case "number":
                errors = Object.assign(errors, this.validateNumber(object, mapping, atKey));
                break;
            case "boolean":
                errors = Object.assign(errors, this.validateBoolean(object, mapping, atKey));
                break;
            case "model":
                errors = Object.assign(errors, this.validateModel(object, mapping, atKey));
                break;
            case "date":
                errors = Object.assign(errors, this.validateDate(object, mapping, atKey));
                break;
            case "any":
                break;
            default:
                throw new WidgetError_1.default(`Unhandled config mapping type '${mapping.type}'.`);
        }
        return errors;
    }
    /**
     * Validates array.
     *
     * @param object
     * @param mapping
     * @param primaryObject
     * @param {string[]} atKey
     * @return {any}
     */
    validateArray(object, mapping, primaryObject, atKey = []) {
        let errors = [];
        if (!Array.isArray(object)) {
            errors.push(new FieldValidationError_1.default(`Expected 'array' got '${typeof object}'.`, FieldValidationTypes_1.FieldValidationTypes.ARRAY_NOT, "array", atKey.join(".")));
        }
        else {
            if (mapping.min !== undefined && mapping.min !== null && object.length < mapping.min) {
                errors.push(new FieldValidationError_1.default(`Array must have atleast ${mapping.min} elements.`, FieldValidationTypes_1.FieldValidationTypes.ARRAY_MIN, "array", atKey.join(".")));
            }
            if (mapping.max !== undefined && mapping.max !== null && object.length > mapping.max) {
                errors.push(new FieldValidationError_1.default(`Array must have atmost ${mapping.max} elements.`, FieldValidationTypes_1.FieldValidationTypes.ARRAY_MAX, "array", atKey.join(".")));
            }
        }
        let actualErrors = {};
        if (mapping.value && Array.isArray(object)) {
            for (let i = 0; i < object.length; ++i) {
                actualErrors = Object.assign(actualErrors, this.doValidate(object[i], mapping.value, primaryObject, atKey.push(i.toString()) && atKey));
                atKey.pop();
            }
        }
        if (errors.length) {
            actualErrors[atKey.join(".")] = errors;
        }
        return actualErrors;
    }
    /**
     * Validates object.
     *
     * @param object
     * @param mapping
     * @param primaryObject
     * @param {string[]} atKey
     * @return {any}
     */
    validateObject(object, mapping, primaryObject, atKey = []) {
        let errors = [];
        if (typeof object !== "object") {
            errors.push(new FieldValidationError_1.default(`Expected 'object' got '${typeof object}'.`, FieldValidationTypes_1.FieldValidationTypes.OBJECT_NOT, "object", atKey.join(".")));
        }
        let actualErrors = {};
        if (mapping.value && typeof object === "object") {
            for (const [key, subMapping] of Object.entries(mapping.value)) {
                const otherErrors = this.doValidate(this.getValueOf(object, key, subMapping), subMapping, primaryObject, atKey.push(key) && atKey);
                atKey.pop();
                actualErrors = Object.assign(actualErrors, otherErrors);
            }
        }
        if (errors.length) {
            actualErrors[atKey.join(".")] = errors;
        }
        return actualErrors;
    }
    /**
     * Validates number.
     *
     * @param object
     * @param mapping
     * @param {string[]} atKey
     * @return {any}
     */
    validateNumber(object, mapping, atKey = []) {
        let errors = [];
        if (typeof object !== "number") {
            errors.push(new FieldValidationError_1.default(`Expected 'number' got '${typeof object}'.`, FieldValidationTypes_1.FieldValidationTypes.NUMBER_NOT, "number", atKey.join(".")));
        }
        else {
            if (mapping.min !== undefined && mapping.min !== null && object < mapping.min) {
                errors.push(new FieldValidationError_1.default(`Value must be greater than ${mapping.min}.`, FieldValidationTypes_1.FieldValidationTypes.NUMBER_MIN, "number", atKey.join(".")));
            }
            if (mapping.max !== undefined && mapping.max !== null && object > mapping.max) {
                errors.push(new FieldValidationError_1.default(`Value must be smaller then ${mapping.max}.`, FieldValidationTypes_1.FieldValidationTypes.NUMBER_MAX, "number", atKey.join(".")));
            }
        }
        return errors.length ? { [atKey.join(".")]: errors } : {};
    }
    /**
     * Validates string.
     *
     * @param object
     * @param mapping
     * @param {string[]} atKey
     * @return {any}
     */
    validateString(object, mapping, atKey = []) {
        let errors = [];
        if (typeof object !== "string") {
            errors.push(new FieldValidationError_1.default(`Expected 'string' got '${typeof object}'.`, FieldValidationTypes_1.FieldValidationTypes.STRING_NOT, "string", atKey.join(".")));
        }
        else {
            if (mapping.min !== undefined && mapping.min !== null && object.length < mapping.min) {
                errors.push(new FieldValidationError_1.default(`String must have atleast ${mapping.min} characters.`, FieldValidationTypes_1.FieldValidationTypes.STRING_MIN, "string", atKey.join(".")));
            }
            if (mapping.max !== undefined && mapping.max !== null && object.length > mapping.max) {
                errors.push(new FieldValidationError_1.default(`String must have atmost ${mapping.max} characters.`, FieldValidationTypes_1.FieldValidationTypes.STRING_MAX, "string", atKey.join(".")));
            }
            if (mapping.regex !== undefined && mapping.regex !== null && !object.match(mapping.regex)) {
                errors.push(new FieldValidationError_1.default(`String does not match regex ${mapping.regex.toString()}.`, FieldValidationTypes_1.FieldValidationTypes.REGEX, "string", atKey.join(".")));
            }
        }
        return errors.length ? { [atKey.join(".")]: errors } : {};
    }
    /**
     * Validates boolean.
     *
     * @param object
     * @param mapping
     * @param {string[]} atKey
     * @return {any}
     */
    validateBoolean(object, mapping, atKey = []) {
        let errors = [];
        if (typeof object !== "boolean") {
            errors.push(new FieldValidationError_1.default(`Expected 'boolean' got '${typeof object}'.`, FieldValidationTypes_1.FieldValidationTypes.BOOLEAN_NOT, "boolean", atKey.join(".")));
        }
        return errors.length ? { [atKey.join(".")]: errors } : {};
    }
    /**
     * Validates nested model base.
     *
     * @param object
     * @param mapping
     * @param {string[]} atKey
     * @return {any}
     */
    validateModel(object, mapping, atKey = []) {
        let errors = [];
        let actualErrors = {};
        let keyConcat = atKey.join(".");
        if (object instanceof MappedModelBase_1.default) {
            errors.push(new FieldValidationError_1.default(`Expected mapped model but got '${typeof object}'.`, FieldValidationTypes_1.FieldValidationTypes.MODEL_NOT, "model", atKey.join(".")));
        }
        else {
            try {
                this.validate(object, null, mapping);
            }
            catch (e) {
                if (ValidationError_1.default.is(e)) {
                    Object.entries(e.getValidationErrors()).forEach(([key, value]) => {
                        actualErrors[`${keyConcat}.${key}`] = value;
                    });
                }
                else {
                    throw e;
                }
            }
        }
        if (errors.length) {
            actualErrors[keyConcat] = errors;
        }
        return actualErrors;
    }
    /**
     * Validates date
     *
     * @param object
     * @param mapping
     * @param {string[]} atKey
     * @return {any}
     */
    validateDate(object, mapping, atKey = []) {
        let errors = [];
        if (typeof object !== "string") {
            errors.push(new FieldValidationError_1.default(`Expected 'string' got '${typeof object}'.`, FieldValidationTypes_1.FieldValidationTypes.STRING_NOT, "date", atKey.join(".")));
        }
        else {
            if (!object.match(/^\d{4}-\d{1,2}-\d{1,2}$/)) {
                errors.push(new FieldValidationError_1.default(`Date does not match format Y-m-d.`, FieldValidationTypes_1.FieldValidationTypes.DATE_NOT, "date", atKey.join(".")));
            }
            else {
                let dateString = object.toString();
                let dateComponents = dateString.split("-");
                let year = Number.parseInt(dateComponents[0], 10);
                // Javascript months start from 0 and go to 11
                let month = Number.parseInt(dateComponents[1], 10) - 1;
                let day = Number.parseInt(dateComponents[2], 10);
                let composedDate = new Date(year, month, day);
                if (!composedDate || composedDate.getDate() != day || composedDate.getMonth() != month ||
                    composedDate.getFullYear() != year) {
                    errors.push(new FieldValidationError_1.default(`Invalid date provided.`, FieldValidationTypes_1.FieldValidationTypes.DATE, "date", atKey.join(".")));
                }
                else {
                    if (mapping.min && composedDate < new Date(mapping.min)) {
                        errors.push(new FieldValidationError_1.default(`The provided date must be greater then ${mapping.min}.`, FieldValidationTypes_1.FieldValidationTypes.DATE_MIN, "date", atKey.join(".")));
                    }
                    else if (mapping.max && composedDate > new Date(mapping.max)) {
                        errors.push(new FieldValidationError_1.default(`The provided date must be less then ${mapping.max}.`, FieldValidationTypes_1.FieldValidationTypes.DATE_MAX, "date", atKey.join(".")));
                    }
                }
            }
        }
        return errors.length ? { [atKey.join(".")]: errors } : {};
    }
    /**
     * Gets the value from object at key based on mapping.
     *
     * @param {MappedModelBase} object
     * @param {string} key
     * @param {*} mapping
     * @return {*}
     */
    getValueOf(object, key, mapping) {
        let getter = null;
        if (mapping.getter === true) {
            getter = `get` + key.charAt(0).toUpperCase() + key.slice(1);
        }
        if (typeof mapping.getter === "string") {
            getter = mapping.getter;
        }
        if (getter === null) {
            return object[key];
        }
        if (typeof object[getter] !== "function") {
            throw new WidgetError_1.default(`Object doesn't implement getter method: '${getter}'`);
        }
        return object[getter]();
    }
}
exports.default = MappingValidator;
